import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Check, X, MapPin, Building2, Handshake } from "lucide-react";

const Avatar = ({ src, name }) => (
  src ? <img src={src} alt={name} className="w-11 h-11 rounded-xl object-cover border border-slate-200" />
      : <div className="w-11 h-11 rounded-xl bg-slate-100 flex items-center justify-center text-slate-400"><Building2 size={22} /></div>
);

const Card = ({ b, children }) => (
  <div className="bg-white border border-slate-200 rounded-2xl p-4 flex items-start gap-3" data-testid={`conn-card-${b.id}`}>
    <Avatar src={b.logo} name={b.name} />
    <div className="min-w-0 flex-1">
      <div className="font-semibold text-slate-900 truncate">{b.name}</div>
      <div className="text-xs text-slate-500 flex items-center gap-2 mt-0.5">
        {b.city && <span className="inline-flex items-center gap-1"><MapPin size={11} />{b.city}</span>}
        {b.category_name && <span>· {b.category_name}</span>}
      </div>
      {b.phone && <div className="text-xs text-slate-600 mt-1">📞 {b.phone}</div>}
      {b.email && <div className="text-xs text-slate-600">✉️ {b.email}</div>}
      {b.message && <p className="text-sm text-slate-600 mt-1 italic">“{b.message}”</p>}
      {children}
    </div>
  </div>
);

export default function ProNetworkConnections() {
  const [data, setData] = useState({ connected: [], incoming: [], outgoing: [] });
  const load = () => api.get("/pro/network/connections").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const respond = async (cid, action) => {
    try { await api.post(`/pro/network/connections/${cid}/respond`, { action });
      toast.success(action === "accept" ? "Connexion acceptée" : "Demande refusée"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  return (
    <div className="space-y-8" data-testid="pro-network-connections">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-slate-900">Mes connexions</h1>
        <p className="text-slate-500 text-sm mt-1">Gérez vos partenaires et les demandes de connexion de votre réseau pro.</p>
      </div>

      {data.incoming.length > 0 && (
        <section>
          <h2 className="font-semibold text-slate-800 mb-3">Demandes reçues ({data.incoming.length})</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {data.incoming.map((b) => (
              <Card key={b.connection_id} b={b}>
                <div className="flex gap-2 mt-2">
                  <Button size="sm" onClick={() => respond(b.connection_id, "accept")} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`conn-accept-${b.id}`}><Check size={14} className="mr-1" />Accepter</Button>
                  <Button size="sm" variant="outline" onClick={() => respond(b.connection_id, "decline")} data-testid={`conn-decline-${b.id}`}><X size={14} className="mr-1" />Refuser</Button>
                </div>
              </Card>
            ))}
          </div>
        </section>
      )}

      <section>
        <h2 className="font-semibold text-slate-800 mb-3 inline-flex items-center gap-2"><Handshake size={18} className="text-[#E05638]" />Mes partenaires ({data.connected.length})</h2>
        {data.connected.length === 0 ? (
          <div className="text-sm text-slate-400 bg-slate-50 rounded-xl p-4">Aucune connexion pour le moment. Rendez-vous dans l'annuaire pour trouver des partenaires.</div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">{data.connected.map((b) => (
            <Card key={b.id} b={b}>
              <Link to={`/reseau/messages?to=${b.id}`} data-testid={`conn-message-${b.id}`} className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-[#E05638] hover:underline">
                <Handshake size={13} /> Envoyer un message
              </Link>
            </Card>
          ))}</div>
        )}
      </section>

      {data.outgoing.length > 0 && (
        <section>
          <h2 className="font-semibold text-slate-800 mb-3">Demandes envoyées ({data.outgoing.length})</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {data.outgoing.map((b) => <Card key={b.connection_id} b={b}><div className="text-xs text-amber-600 mt-1">En attente de réponse…</div></Card>)}
          </div>
        </section>
      )}
    </div>
  );
}
